import { createReducer, original, type Draft, type Reducer } from "@reduxjs/toolkit";
import arrayMove from "array-move";
import { isEqual, pick } from "es-toolkit";

import { assert } from "../../asserts/index.ts";
import {
  referencedNodeValue,
  triggerActionSchema,
  type AddNodeAction,
  type DesignerNodeData,
  type MultilingualNodeConfig,
  type NodeAction,
  type Slot,
  type SlotId,
} from "../../schemas/index.ts";
import { isNodeId, isReferenceVariable, removeTranslationCreate } from "../../shared/index.ts";
import type { VariableConfig } from "../../types/index.ts";
import { InvalidActionError } from "../InvalidActionError.ts";
import type { NodeDesignsState, SlotsByParentId } from "../types.ts";
import {
  addMultiValueSlot,
  addNodeAction,
  changeNodeTypeId,
  moveNodeAction,
  removeLanguageAction,
  removeNodeAction,
  reorderNodeAction,
  replaceNodeAction,
  setNodeRoleAction,
  setOwnerAction,
  updateNodeConfigAction,
  updateVersionsAction,
} from "./actions.ts";
import type { NodeEvent, NotifyNodeDesignsEvent } from "./events.ts";
import { repointNodeReferences, replacementRepointsReferences } from "./repointNodeReferences.ts";
import { getTriggerIds, triggersInitialState, triggersReducerCreate } from "./triggersReducer/index.ts";

export const emptyInitialNodeDesigns: NodeDesignsState = {
  nextId: 1,
  designsById: {},
  slotsByParentId: {},
  parentIdByChildId: {},
  versionByTypeId: {},
  triggerDesigns: triggersInitialState,
  upstreamTemplateIdById: {},
};

interface ClientOptions {
  canMoveHere?: (state: NodeDesignsState) => (nodeId: number, to: SlotId) => boolean;
}

export interface NodeDesignsReducerOptions {
  clientOptions?: ClientOptions; // optional prop; not provided on server as needs manifest awareness. used for validation of moves etc
  notifyEvent?: NotifyNodeDesignsEvent;
}

const slotDescr = (slot: SlotId) => `${slot.parentId}:${slot.name}`;

const slotNodeIds = (slot: Slot): number[] => [slot.residentId, slot.placeholderId].filter((id): id is number => !!id);

const isNodeIdValidCreate = (state: Draft<NodeDesignsState>) => (nodeId: number) => nodeId in state.designsById;

const checkSlotId = (slotId: SlotId, { action, message }: { action: NodeAction; message: string }) => {
  if ("index" in slotId && slotId.index !== undefined && slotId.index < 0) {
    throw new InvalidActionError(
      action,
      `Slot id must have a non-negative index: ${slotDescr(slotId)}. ${message ? ` Context: ${message})` : ""}`,
    );
  }
};

export type NodeDesignsReducer = Reducer<NodeDesignsState, NodeAction, NodeDesignsState>;

export const nodeDesignsReducerCreate = (options: NodeDesignsReducerOptions = {}) => {
  const { clientOptions, notifyEvent } = options;

  const addNodeCreate = (action: NodeAction) => (state: Draft<NodeDesignsState>, details: AddNodeAction["payload"]) => {
    const { designsById, parentIdByChildId, slotsByParentId, versionByTypeId } = state;

    const triggersReducer = triggersReducerCreate({ notifyEvent, isNodeIdValid: isNodeIdValidCreate(state) });

    const addedNodes: DesignerNodeData[] = [];

    const addToSlot = (slotId: SlotId, childIdOrIds: number | number[], isPlaceholder?: boolean) => {
      const { parentId, name } = slotId;

      if (!slotsByParentId[parentId]) {
        slotsByParentId[parentId] = {};
      }

      const existingSlotOrSlots = slotsByParentId[parentId][name];
      if (
        existingSlotOrSlots &&
        !Array.isArray(existingSlotOrSlots) &&
        existingSlotOrSlots.residentId &&
        !isPlaceholder
      ) {
        throw new InvalidActionError(action, `Entry already exists in single-entry slot: ${parentId}:${name}.`);
      }

      const newChildIds = Array.isArray(childIdOrIds) ? childIdOrIds : [childIdOrIds];

      const newSlots = isPlaceholder
        ? newChildIds.map((id): Slot => ({ placeholderId: id }))
        : newChildIds.map((id): Slot => ({ residentId: id }));

      // basic rule is if initial slot value is [] then treat it as an array
      // otherwise assume it's one object to edit
      // at some stage we may be able to infer multi-value slots from manifests
      // but for now a node designer needs to provide initial children as an array even if [] to support multi-value slots

      if (
        (!existingSlotOrSlots && !Array.isArray(childIdOrIds)) ||
        (existingSlotOrSlots && !Array.isArray(existingSlotOrSlots))
      ) {
        slotsByParentId[parentId][name] = { ...existingSlotOrSlots, ...newSlots[0] };
      } else {
        const slots = [...(existingSlotOrSlots ?? [])];
        if (slotId.index !== undefined && slotId.index > slots.length) {
          // without this check we get no error but nothing happens. splice just quietly fails
          throw new InvalidActionError(action, `Slot index past end of array: ${slotDescr(slotId)}`);
        }
        const index = slotId.index ?? existingSlotOrSlots?.length ?? 0;

        slots.splice(index, 0, ...newSlots);
        slotsByParentId[parentId][name] = slots;
      }

      for (const childId of [childIdOrIds].flat()) {
        parentIdByChildId[childId] = parentId;
      }
    };

    const add = ({ node, slotId }: AddNodeAction["payload"]) => {
      const { id, typeId, config, placeholder, version, isPlaceholder, owner, creator, role } = node;
      if (designsById[id]) {
        throw new InvalidActionError(action, `Duplicate: id already exists with id: ${id}.`);
      }
      if (placeholder) {
        if (!placeholder.isPlaceholder) {
          throw new InvalidActionError(action, `Placeholder for node is not a placeholder node type.`);
        }
        add({ node: placeholder, slotId });
      }

      designsById[id] = { typeId, config, creator, owner, role: role };

      if (!version) {
        throw new InvalidActionError(action, `Nodes to be added must have a version specified. ${typeId}:${id}`);
      }

      if (!(typeId in versionByTypeId)) {
        versionByTypeId[typeId] = version;
      }

      if (versionByTypeId[typeId] !== version) {
        throw new InvalidActionError(
          action,
          `Attempt to add node of type ${typeId} with version ${version} where version for that type is ${versionByTypeId[typeId]}.`,
        );
      }

      if (slotId) {
        addToSlot(slotId, id, isPlaceholder);
      }

      if (notifyEvent) {
        notifyEvent({ type: "NODE_ADDED", id, typeId, role, config });
      }

      for (const [name, entry] of Object.entries(node.childNodes ?? {})) {
        const childSlotId: SlotId = { name, parentId: id };
        if (Array.isArray(entry)) {
          addToSlot(childSlotId, []); // make sure we know it's an array
          for (const child of entry) {
            add({ node: child, slotId: childSlotId });
          }
        } else if (entry) {
          add({ node: entry, slotId: childSlotId });
        }
      }

      addedNodes.push(node);
    };

    add(details);

    const maxId = Math.max(...addedNodes.map((n) => n.id));
    if (state.nextId <= maxId) {
      state.nextId = maxId + 1;
    }

    // defer to after adding nodes as conditions and effects can reference active nodes down the tree
    for (const node of addedNodes) {
      if (node.triggers) {
        // TODO: make this into a separate action? It's a little misleading as we've split an ADD into lots of little ones
        // to keep it simple for the triggers reducer
        state.triggerDesigns = triggersReducer(state.triggerDesigns, addNodeAction({ node }));

        const maxTriggerId = Math.max(...getTriggerIds(state.triggerDesigns));
        if (state.nextId <= maxTriggerId) {
          state.nextId = maxTriggerId + 1;
        }
      }
      if (node.upstreamTemplateId) {
        state.upstreamTemplateIdById[node.id] = node.upstreamTemplateId;
      }
    }
  };

  const removeNode = (state: Draft<NodeDesignsState>, id: number) => {
    const { designsById, parentIdByChildId, slotsByParentId, versionByTypeId } = state;

    const topDesign = designsById[id];
    if (!topDesign) {
      return;
    }

    const removed: Omit<Extract<NodeEvent, { type: "NODE_REMOVED" }>, "type">[] = [];

    const disconnectReferringVars = (nodeId: number) => {
      const refIdPrefix = `${nodeId}:`;
      for (const { config } of Object.values(designsById)) {
        if (!("variables" in config && typeof config.variables === "object")) {
          continue;
        }
        for (const [name, variable] of Object.entries(config.variables)) {
          if (isReferenceVariable(variable)) {
            if ("refId" in variable && variable.refId.startsWith(refIdPrefix)) {
              const { variables = {} } = designsById[nodeId].config as { variables: Record<string, VariableConfig> };
              const varName = variable.refId.split(":")[1];
              if (!(varName in variables)) {
                // could be assert. should be caught higher up
                throw new Error(
                  `Reference variable with refId ${variable.refId} not found. Reference variables need to be config variables.`,
                );
              }
              config.variables[name] = variables[varName]; // works for multi level references
            }
          }
        }
      }
    };

    const disconnectConfigReferences = (nodeId: number) => {
      for (const { config } of Object.values(designsById)) {
        if (!("nodeReferences" in config && typeof config.nodeReferences === "object")) continue;
        for (const [name, referenceIdOrIds] of Object.entries(config.nodeReferences)) {
          if (Array.isArray(referenceIdOrIds) && referenceIdOrIds.includes(nodeId)) {
            config.nodeReferences[name] = referenceIdOrIds.filter((referenceId) => referenceId !== nodeId);
          } else {
            if (referenceIdOrIds === nodeId) {
              delete config.nodeReferences[name];
            }
          }
        }
      }
    };

    // A deleted node's inline node-value references are not mirrored into
    // `nodeReferences`, so clean them here. Dropping the entry lets any binding
    // fall back to its default.
    const disconnectValueReferences = (nodeId: number) => {
      for (const { config } of Object.values(designsById)) {
        if (!config.values) continue;
        for (const [name, value] of Object.entries(config.values)) {
          // entries may be a raw literal ("the value is right here") or a ReferencedValue
          if (referencedNodeValue(value)?.nodeId === nodeId) {
            delete config.values[name];
          }
        }
      }
    };

    const removeSubtree = (nodeId: number) => {
      const design = designsById[nodeId];
      if (!design) return;

      const removedConfig = original(design.config) as typeof design.config;

      disconnectReferringVars(nodeId);
      disconnectConfigReferences(nodeId);
      disconnectValueReferences(nodeId);

      delete designsById[nodeId];

      // check if only one of this typeid
      if (!Object.values(designsById).find((v) => v.typeId === design.typeId)) {
        delete versionByTypeId[design.typeId];
      }

      removed.unshift({ id: nodeId, typeId: design.typeId, role: design.role, config: removedConfig });

      const slots: Slot[] = Object.values(slotsByParentId[nodeId] ?? {}).flat();
      const idsToRemove = slots
        .map(({ residentId, placeholderId }): number[] => [residentId, placeholderId].filter(isNodeId))
        .flat();
      for (const idToRemove of idsToRemove) {
        removeSubtree(idToRemove);
        delete parentIdByChildId[idToRemove];
      }
      delete slotsByParentId[nodeId];
    };

    removeSubtree(id);

    const removeFromSlotIfMatches = (slot: Slot) => {
      const { residentId, placeholderId } = slot;
      if (residentId === id) {
        delete slot.residentId;
      } else if (placeholderId === id) {
        delete slot.placeholderId;
      }
      return slot;
    };

    // remove self from parent's childIds lookups, from parentId lookup
    const parentId = parentIdByChildId[id];
    if (parentId) {
      const slotsByName = slotsByParentId[parentId] ?? {};

      for (const [name, slots] of Object.entries(slotsByName)) {
        if (Array.isArray(slots)) {
          slotsByName[name] = slots
            .map(removeFromSlotIfMatches)
            .filter((slot) => slot.residentId || slot.placeholderId);
        } else {
          removeFromSlotIfMatches(slots);
          if (Object.keys(slots).length === 0) {
            delete slotsByName[name];
          }
        }
      }

      if (Object.keys(slotsByParentId[parentId]).length === 0) {
        delete slotsByParentId[parentId];
      }

      delete parentIdByChildId[id];
    }

    for (const node of removed) {
      delete state.upstreamTemplateIdById[node.id];
    }

    const triggersReducer = triggersReducerCreate({ notifyEvent, isNodeIdValid: isNodeIdValidCreate(state) });
    state.triggerDesigns = removed
      .map(({ id: nodeId }) => removeNodeAction({ id: nodeId }))
      .reduce(triggersReducer, state.triggerDesigns);

    if (notifyEvent) {
      for (const { id: nodeId, typeId, role, config } of removed) {
        notifyEvent({ type: "NODE_REMOVED", id: nodeId, typeId, role, config });
      }
    }
  };

  return createReducer(emptyInitialNodeDesigns, (builder) => {
    builder.addCase(addNodeAction, (state, action) => {
      addNodeCreate(action)(state, action.payload);
    });

    builder.addCase(updateNodeConfigAction, (state, action) => {
      const { id, config } = action.payload;
      const { designsById } = state;
      const entry = designsById[id];
      if (!entry) {
        throw new InvalidActionError(action, `Node with id ${id} not found to update.`);
      }
      if (notifyEvent) {
        // @ts-ignore skip excessively deep type instantiation error due to immer stuffs
        const oldConfig = original(entry.config) as MultilingualNodeConfig;
        const { typeId, role } = entry;
        notifyEvent({
          type: "NODE_UPDATED",
          id,
          typeId,
          role,
          newConfig: config,
          oldConfig: oldConfig,
        });
      }
      entry.config = config;
    });

    builder.addCase(removeNodeAction, (state, { payload: { id } }) => {
      removeNode(state, id);
    });

    builder.addCase(replaceNodeAction, (state, action) => {
      const {
        payload: { id: idToReplace, replacement },
      } = action;
      if (!(idToReplace in state.designsById)) {
        throw new InvalidActionError(action, `No node found with id ${idToReplace} to replace.`);
      }
      // add first with no parent, sort that out below
      addNodeCreate(action)(state, { node: replacement });

      const { parentIdByChildId, slotsByParentId } = state;
      const { id, isPlaceholder } = replacement;

      const parentId = parentIdByChildId[idToReplace];
      if (!parentId) {
        throw new InvalidActionError(action, `Cannot replace root node.`);
      }

      const slotsByName = slotsByParentId[parentId];

      let found = false;
      const idsToBeRemoved: number[] = [];
      for (const [name, slotsOrSlots] of Object.entries(slotsByName)) {
        if (Array.isArray(slotsOrSlots)) {
          slotsByName[name] = slotsOrSlots.map((slot) => {
            const { residentId, placeholderId } = slot;
            if (residentId === idToReplace || placeholderId === idToReplace) {
              found = true;
              if ((residentId === idToReplace && !isPlaceholder) || (placeholderId === idToReplace && isPlaceholder)) {
                idsToBeRemoved.push(idToReplace);
              }
              if (isPlaceholder) {
                return {
                  placeholderId: id,
                };
              }
              return {
                ...slot,
                residentId: id,
              };
            }
            return slot;
          });
        } else {
          const slot = slotsOrSlots;
          const { residentId, placeholderId } = slot;
          if (residentId === idToReplace || placeholderId === idToReplace) {
            found = true;
            if ((residentId === idToReplace && !isPlaceholder) || (placeholderId === idToReplace && isPlaceholder)) {
              idsToBeRemoved.push(idToReplace);
            }
            if (isPlaceholder) {
              slot.placeholderId = id;
              delete slot.residentId;
            } else {
              slot.residentId = id;
              // don't delete placeholder as want for "purpose" feature
            }
          }
        }
      }
      if (!found) {
        throw new Error(`Code error ${idToReplace} found in designs and parentByChild but not found in slots.`);
      }

      parentIdByChildId[id] = parentId;

      if (idsToBeRemoved.includes(idToReplace) && replacementRepointsReferences(action.payload)) {
        repointNodeReferences(state, idToReplace, id);
      }

      for (const idToBeRemoved of idsToBeRemoved) {
        delete parentIdByChildId[idToBeRemoved];

        // we've only removed the parentage by replacing it
        // now we need to remove the node and descendants
        removeNode(state, idToBeRemoved);
      }
    });

    builder.addCase(reorderNodeAction, (state, action) => {
      const { id, newIndex } = action.payload;

      if (!(id in state.designsById)) {
        throw new InvalidActionError(action, `No node found with id ${id} to move.`);
      }
      if (!(id in state.parentIdByChildId)) {
        throw new InvalidActionError(action, `Node with id ${id} not in a slot.`);
      }
      if (newIndex < 0) {
        throw new InvalidActionError(action, "New index must be 0 or greater.");
      }

      const slotHasNode = (slot: Slot) => slot.residentId === id || slot.placeholderId === id;

      const parentId = state.parentIdByChildId[id];
      const parentSlotsByName = state.slotsByParentId[parentId];
      let fromSlotId: SlotId | undefined;
      let toSlotId: SlotId | undefined;
      for (const [name, slots] of Object.entries(parentSlotsByName)) {
        if (!Array.isArray(slots)) {
          if (slotHasNode(slots)) {
            throw new InvalidActionError(action, `Node with id ${id} not in a multi-value slot to reorder`);
          }
          continue;
        }
        const slotToMoveIndex = slots.findIndex(slotHasNode);
        if (slotToMoveIndex < 0) {
          continue;
        }
        fromSlotId = { parentId, name, index: slotToMoveIndex };
        toSlotId = { parentId, name, index: newIndex };
        parentSlotsByName[name] = arrayMove(slots, slotToMoveIndex, newIndex);
      }

      if (notifyEvent && fromSlotId && toSlotId && !isEqual(fromSlotId, toSlotId)) {
        notifyEvent({ type: "NODE_SLOT_MOVED", from: fromSlotId, to: toSlotId });
      }
    });

    builder.addCase(updateVersionsAction, (state, { payload: { versions } }) => {
      for (const [typeId, version] of versions) {
        state.versionByTypeId[typeId] = version;
      }
    });

    builder.addCase(moveNodeAction, (state, action) => {
      const { from, to } = action.payload;
      checkSlotId(from, { action, message: "Move from" });
      checkSlotId(to, { action, message: "Move to" });

      if (isEqual(pick(from, ["parentId", "name"]), pick(to, ["parentId", "name"]))) {
        throw new InvalidActionError(action, `Move not supported on same slot. Use reorder instead.`);
      }

      const { slotsByParentId } = state;

      const fromSlotsByName = slotsByParentId[from.parentId];
      if (!fromSlotsByName) {
        if (!state.designsById[from.parentId]) {
          throw new InvalidActionError(action, `Node for move from slot doesn't exist: ${from.parentId}.`);
        }
        throw new InvalidActionError(action, `No slots on node for move from slot with id: ${from.parentId}.`);
      }
      const fromSlot = fromSlotsByName[from.name];
      if (!fromSlot) {
        throw new InvalidActionError(action, `Slot to move not found: ${slotDescr(from)}.`);
      }

      if (clientOptions?.canMoveHere) {
        const canMoveHere = clientOptions.canMoveHere(state);
        const nodeIds = (
          Array.isArray(fromSlot) ? (from.index !== undefined ? [fromSlot[from.index]] : fromSlot) : [fromSlot]
        ).flatMap(slotNodeIds);
        const cannotMoveHere = nodeIds.find((id) => !canMoveHere(id, to));
        if (cannotMoveHere) {
          throw new InvalidActionError(
            action,
            `Node cannot be moved there as linkages exist in it's current position that would be invalidated.`,
          );
        }
      }

      if (!slotsByParentId[to.parentId]) {
        if (!state.designsById[to.parentId]) {
          throw new InvalidActionError(action, `Node for move to slot doesn't exist ${to.parentId}.`);
        }
        slotsByParentId[to.parentId] = {};
      }
      const toSlotsByName = slotsByParentId[to.parentId];
      if (toSlotsByName[to.name]) {
        const toSlotOrSlots = toSlotsByName[to.name];
        if (Array.isArray(toSlotOrSlots)) {
          if (Array.isArray(fromSlot)) {
            if (from.index === undefined) {
              throw new InvalidActionError(
                action,
                `Index must be specified for source slot on multi-value slots moving to another multi-value slot.`,
              );
            }
            if (to.index === undefined) {
              toSlotOrSlots.push(fromSlot[from.index]);
            } else {
              // slot it in at index, pushing current occupant down one
              toSlotsByName[to.name] = [
                ...toSlotOrSlots.slice(0, to.index),
                fromSlot[from.index],
                ...toSlotOrSlots.slice(to.index),
              ];
            }
          } else {
            toSlotOrSlots.push(fromSlot);
          }
        } else {
          throw new InvalidActionError(action, `Target slot already occupied: ${slotDescr(to)}.`);
        }
      } else {
        if (Array.isArray(fromSlot) && from.index !== undefined) {
          toSlotsByName[to.name] = fromSlot[from.index];
        } else {
          toSlotsByName[to.name] = fromSlot;
        }
      }

      const isDescendant = (id: number, ofId: number): boolean => {
        const parentId = state.parentIdByChildId[id];
        if (!parentId) {
          return false;
        }
        if (parentId === ofId) {
          return true;
        }
        assert(parentId !== id, "Node is parent of itself.");
        return isDescendant(parentId, ofId);
      };

      const updateParentIdByChildIds = (ids: number[], parentId: number) => {
        for (const id of ids) {
          if (isDescendant(parentId, id)) {
            throw new Error(`Cannot move a node onto a descendant.`);
          }
          state.parentIdByChildId[id] = parentId;
        }
      };

      if (Array.isArray(fromSlotsByName[from.name]) && from.index !== undefined) {
        const elements = fromSlotsByName[from.name] as Slot[];
        if (from.index > elements.length - 1) {
          throw new InvalidActionError(action, `Out of range index: ${from.index}`);
        }
        const removedElement = elements[from.index];
        fromSlotsByName[from.name] = [...elements.slice(0, from.index), ...elements.slice(from.index + 1)];
        const ids = [removedElement.residentId, removedElement.placeholderId].filter((id): id is number => !!id);
        updateParentIdByChildIds(ids, to.parentId);
      } else {
        const entryOrEntries = fromSlotsByName[from.name];
        delete fromSlotsByName[from.name];
        const ids = (Array.isArray(entryOrEntries) ? entryOrEntries : [entryOrEntries]).flatMap((e) =>
          [e.residentId, e.placeholderId].filter((id): id is number => !!id),
        );
        updateParentIdByChildIds(ids, to.parentId);
      }

      if (Object.keys(slotsByParentId[from.parentId]).length === 0) {
        delete slotsByParentId[from.parentId];
      }

      if (notifyEvent) {
        notifyEvent({ type: "NODE_SLOT_MOVED", from, to });
      }
    });

    builder.addCase(addMultiValueSlot, (state, action) => {
      const {
        slotId: { parentId, name },
      } = action.payload;

      const { designsById, slotsByParentId } = state;
      if (!(parentId in designsById)) {
        throw new Error(`Node not found with id: ${parentId}`);
      }
      if (slotsByParentId[parentId] && name in slotsByParentId[parentId]) {
        throw new Error(`Slot already exists on ${parentId} with name ${name}`);
      }
      if (!slotsByParentId[parentId]) {
        slotsByParentId[parentId] = {};
      }
      state.slotsByParentId[parentId][name] = [];
    });

    builder.addCase(changeNodeTypeId, (state, action) => {
      const { oldTypeId, newTypeId } = action.payload;
      state.designsById = Object.fromEntries(
        Object.entries(state.designsById).map(([id, design]) => [
          id,
          design.typeId === oldTypeId
            ? {
                ...design,
                typeId: newTypeId,
              }
            : design,
        ]),
      );
      state.versionByTypeId = Object.fromEntries(
        Object.entries(state.versionByTypeId).map(([typeId, version]) => [
          typeId === oldTypeId ? newTypeId : typeId,
          version,
        ]),
      );
    });

    builder.addCase(setNodeRoleAction, (state, action) => {
      const { id, role } = action.payload;

      if (!(id in state.designsById)) {
        throw new Error(`Node not found with id: ${id}`);
      }

      state.designsById[id].role = role;
    });

    builder.addCase(removeLanguageAction, (state, action) => {
      const { lang } = action.payload;
      const removeTranslation = removeTranslationCreate({ lang });
      for (const [id, entry] of Object.entries(state.designsById)) {
        // @ts-ignore skip excessively deep type instantiation error due to immer stuffs
        const oldConfig = original(entry.config);
        const newConfig = removeTranslation(oldConfig);
        // removeTranslationCreate rebuilds every object it walks, so compare rather than trust identity
        if (isEqual(oldConfig, newConfig)) continue;

        entry.config = newConfig;
        if (notifyEvent) {
          const { typeId, role } = entry;
          notifyEvent({ type: "NODE_UPDATED", id: Number(id), typeId, role, newConfig, oldConfig });
        }
      }
    });

    builder.addCase(setOwnerAction, (state, { payload: { id: rootId, owner } }) => {
      const recurseSetOwner = (nodeId: number) => {
        // remove unnecessary entries since user is implied when not specified
        const entry = state.designsById[nodeId];
        if (owner === "user") {
          delete entry.owner;
        } else {
          entry.owner = owner;
        }

        const nodeIds = Object.values((state.slotsByParentId[nodeId] ?? {}) as SlotsByParentId).flatMap(
          ({ residentId, placeholderId }) => [residentId, placeholderId].map(Number).filter(isNodeId),
        );
        for (const childId of nodeIds) {
          recurseSetOwner(childId);
        }
      };

      recurseSetOwner(rootId);
    });

    builder.addDefaultCase((state, inputAction) => {
      const originalState = state;
      const triggersReducer = triggersReducerCreate({ notifyEvent, isNodeIdValid: isNodeIdValidCreate(originalState) });
      const { data: action, success } = triggerActionSchema.safeParse(inputAction);
      if (!(success && action)) return;

      const triggerDesigns = triggersReducer(originalState.triggerDesigns ?? triggersInitialState, action);
      const maxTriggerId = Math.max(...getTriggerIds(triggerDesigns));
      state.nextId = Math.max(state.nextId, maxTriggerId + 1);
      state.triggerDesigns = triggerDesigns;
    });
  });
};
